import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Sparkles, RotateCcw, ExternalLink, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AssistantImagesPanel } from "@/components/AssistantImagesPanel";

const newSessionId = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const ActionCard = ({ result }) => {
  if (!result) return null;
  if (!result.ok) {
    return (
      <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="assistant-action-error">
        {result.error}
      </div>
    );
  }
  const status = result.scheduled_for
    ? `programată pentru ${new Date(result.scheduled_for).toLocaleString("ro-RO", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}`
    : result.published
      ? "publicată pe blog"
      : result.type === "update_post"
        ? "actualizată"
        : "salvată ca ciornă";
  return (
    <div className="mt-3 rounded-xl border border-[color-mix(in_srgb,var(--teal)_30%,transparent)] bg-[var(--tint-mint)] px-4 py-3" data-testid="assistant-action-card">
      <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--teal-dark)]">
        {result.type === "update_post" ? "Însemnare modificată" : "Însemnare creată"} · {status}
      </p>
      <p className="mt-1 font-serif text-base text-[#F7F1E8]">{result.title}</p>
      <div className="mt-2 flex flex-wrap gap-3">
        <Link
          to={`/admin/edit/${result.post_id}`}
          data-testid="assistant-open-editor-link"
          className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.15em] text-[var(--accent)] underline underline-offset-4"
        >
          <PencilLine className="h-3.5 w-3.5" strokeWidth={1.8} /> Deschide în editor
        </Link>
        {result.published && (
          <a
            href={`/post/${result.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.15em] text-[var(--teal-dark)] underline underline-offset-4"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.8} /> Vezi pe blog
          </a>
        )}
      </div>
      <AssistantImagesPanel postId={result.post_id} />
    </div>
  );
};

export const AdminAssistantPage = () => {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("assistant_session") || newSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("assistant_session", sessionId);
    api
      .get(`/admin/assistant/history/${sessionId}`)
      .then((r) => setMessages(r.data))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/admin/assistant/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply, action_result: data.action_result }]);
      if (data.action_result?.ok) toast.success("Am făcut modificarea pe blog!");
    } catch (e) {
      toast.error(formatApiError(e, "Asistentul nu a putut răspunde"));
    } finally {
      setSending(false);
    }
  };

  const QUICK_PROMPTS = [
    {
      label: "Subiecte căutate pe Google",
      testId: "assistant-quick-seo-topics",
      text:
        "Propune-mi 6 subiecte noi de însemnări pe care oamenii chiar le caută pe Google în România, potrivite temelor blogului meu (psihologie și oameni, relații, viața de adult, bani și carieră, internet și social media). Pentru fiecare dă-mi: un titlu-cârlig prietenos cu Google (să conțină exact cuvintele pe care le-ar tasta cineva în căutare) și o frază despre unghiul meu incisiv, fără menajamente. Evită subiectele despre care am scris deja.",
    },
    {
      label: "Idei de titluri",
      testId: "assistant-quick-titles",
      text:
        "Vreau 3 variante de titlu prietenoase cu Google pentru o însemnare. Întreabă-mă întâi, pe scurt, despre ce e însemnarea.",
    },
    {
      label: "Scrie o însemnare nouă",
      testId: "assistant-quick-new-post",
      text: "Vreau să scriem împreună o însemnare nouă. Întreabă-mă despre ce aș vrea să fie.",
    },
  ];

  const sendQuick = (text) => {
    if (sending) return;
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
    setSending(true);
    api
      .post("/admin/assistant/chat", { session_id: sessionId, message: text })
      .then(({ data }) => {
        setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply, action_result: data.action_result }]);
        if (data.action_result?.ok) toast.success("Am făcut modificarea pe blog!");
      })
      .catch((e) => toast.error(formatApiError(e, "Asistentul nu a putut răspunde")))
      .finally(() => setSending(false));
  };

  const resetSession = () => {
    setSessionId(newSessionId());
    setMessages([]);
  };

  return (
    <main data-testid="admin-assistant-page" className="mx-auto flex h-[calc(100vh-76px)] max-w-4xl flex-col px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/admin"
            data-testid="assistant-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
          </Link>
          <h1 className="mt-2 flex items-center gap-2 font-serif text-3xl font-light text-[#F7F1E8]">
            <Sparkles className="h-6 w-6 text-[var(--accent)]" strokeWidth={1.4} /> Partenerul tău de scris
          </h1>
        </div>
        <button
          onClick={resetSession}
          data-testid="assistant-new-conversation-btn"
          className="inline-flex items-center gap-2 rounded-full border border-[color-mix(in_srgb,var(--milk)_15%,transparent)] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#F7F1E8]/78 hover:border-[var(--accent)] hover:text-[var(--accent)]"
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.8} /> Conversație nouă
        </button>
      </div>

      <div className="mt-6 flex-1 space-y-4 overflow-y-auto rounded-2xl bg-[#2B1B13]/70 p-5 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]" data-testid="assistant-messages">
        {messages.length === 0 && !sending && (
          <div className="py-10 text-center text-sm text-[#F7F1E8]/72" data-testid="assistant-empty-state">
            <p className="font-serif text-xl text-[#F7F1E8]/90">Cu ce te pot ajuta azi?</p>
            <p className="mx-auto mt-3 max-w-md leading-relaxed">
              Îmi poți cere idei, șlefuiri de text sau chiar: <em>„Scrie o însemnare despre de ce ne mințim că „suntem bine” și salveaz-o ca ciornă"</em> — o creez eu direct pe blog.
            </p>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${
                m.role === "user" ? "bg-[var(--accent)] text-white" : "bg-[color-mix(in_srgb,var(--tint-pink)_70%,transparent)] text-[#F7F1E8]"
              }`}
            >
              <p className="whitespace-pre-wrap">{m.content}</p>
              {m.role === "assistant" && <ActionCard result={m.action_result} />}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="assistant-typing">
            <div className="rounded-2xl bg-[color-mix(in_srgb,var(--tint-pink)_70%,transparent)] px-4 py-3 text-sm text-[#F7F1E8]/78">
              Scriu<span className="animate-pulse">…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {QUICK_PROMPTS.map((q) => (
          <button
            key={q.testId}
            data-testid={q.testId}
            onClick={() => sendQuick(q.text)}
            disabled={sending}
            className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--accent)_30%,transparent)] bg-[#2B1B13]/70 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--accent)] transition-colors hover:bg-[var(--tint-pink)] disabled:opacity-50"
          >
            <Sparkles className="h-3 w-3" strokeWidth={1.6} />
            {q.label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex items-end gap-2">
        <textarea
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          placeholder="Scrie-i partenerului tău… (Enter pentru a trimite)"
          className="w-full flex-1 resize-none rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
        />
        <button
          data-testid="assistant-send-btn"
          onClick={send}
          disabled={sending || !input.trim()}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
          aria-label="Trimite mesajul"
        >
          <Send className="h-4 w-4" strokeWidth={1.8} />
        </button>
      </div>
    </main>
  );
};

export default AdminAssistantPage;
